function _toConsumableArray(r) { return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread(); }
function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _iterableToArray(r) { if ("undefined" != typeof Symbol && null != r[Symbol.iterator] || null != r["@@iterator"]) return Array.from(r); }
function _arrayWithoutHoles(r) { if (Array.isArray(r)) return _arrayLikeToArray(r); }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { useMemo } from "react";
import { paginateRange } from "./utils";
export var DOTS = "...";
var MAX_DOTS_ELEMENTS = 2;
var MIN_PAGES_ELEMENTS = ["firstPage", "lastPage", "currentPage"].length;

/**
 * usePagination - Generates the correct page range to display in the pagination
 *
 * @typedef {object} PaginationObj
 * @property {array} paginationRange - List of current pages to display eg. [1, 2, 3, ..., 200]
 * @property {number} totalPageCount - Total number of pages
 * @property {boolean} isFirstPage - True if the current page is the first page
 * @property {boolean} isLastPage - True if the current page is the last page
 *
 * @param {number} totalCount - Total number of items
 * @param {number} itemPerPage - Max items to display per page
 * @param {number} siblingCount - Number of pages surrounding the current page
 * @param {number} currentPage - Current page
 * @return {PaginationObj} The data need to build the pagination
 */
export var usePagination = function usePagination(_ref) {
  var totalCount = _ref.totalCount,
    itemPerPage = _ref.itemPerPage,
    siblingCount = _ref.siblingCount,
    currentPage = _ref.currentPage;
  var totalPageCount = Math.ceil(totalCount / itemPerPage);
  var paginationRange = useMemo(function () {
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + MAX_DOTS_ELEMENTS
    var totalPageNumbers = siblingCount + (MIN_PAGES_ELEMENTS + MAX_DOTS_ELEMENTS);

    /*
      Case 1:
      If the number of pages is less than the total of pages  we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
    if (totalPageNumbers >= totalPageCount) {
      return paginateRange(1, totalPageCount);
    }

    /*
      Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    */
    var leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    var rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    /*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount.
      Hence we are using leftSiblingIndex > MAX_DOTS_ELEMENTS and rightSiblingIndex < totalPageCount - MAX_DOTS_ELEMENTS
    */
    var shouldShowLeftDots = leftSiblingIndex > MAX_DOTS_ELEMENTS;
    var shouldShowRightDots = rightSiblingIndex < totalPageCount;
    var startIndex = 1;
    var endIndex = totalPageCount;

    /*
      Case 2:
      No left dots to show, but rights dots to be shown
    */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      // leftItemCount is determined as  (firstPage + lastPage + currentPage + MAX_DOTS_ELEMENTS) * siblingCount
      var leftItemCount = (MIN_PAGES_ELEMENTS + MAX_DOTS_ELEMENTS) * siblingCount;
      var leftRange = paginateRange(1, leftItemCount);
      if (leftRange.length === 0) {
        leftRange = [currentPage];
      }
      return [].concat(_toConsumableArray(leftRange), [DOTS, totalPageCount]);
    }

    /*
      Case 3: No right dots to show, but left dots to be shown
    */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      // rightItemCount is determined as  (firstPage + lastPage + currentPage + MAX_DOTS_ELEMENTS) * siblingCount
      var rightItemCount = (MIN_PAGES_ELEMENTS + MAX_DOTS_ELEMENTS) * siblingCount;
      var rightRange = paginateRange(totalPageCount - rightItemCount + 1, totalPageCount);
      if (rightRange.length === 0) {
        rightRange = paginateRange(currentPage, totalPageCount);
      }
      return [startIndex, DOTS].concat(_toConsumableArray(rightRange));
    }

    /*
      Case 4: Both left and right dots to be shown
    */
    if (shouldShowLeftDots && shouldShowRightDots) {
      var middleRange = paginateRange(leftSiblingIndex, rightSiblingIndex);
      return [startIndex, DOTS].concat(_toConsumableArray(middleRange), [DOTS, endIndex]);
    }
    return [];
  }, [siblingCount, currentPage, totalPageCount]);
  var firstPage = 1;
  var lastPage = paginationRange[paginationRange.length - 1];
  var isFirstPage = currentPage === firstPage;
  var isLastPage = lastPage === currentPage;
  return {
    paginationRange: paginationRange,
    totalPageCount: totalPageCount,
    isFirstPage: isFirstPage,
    isLastPage: isLastPage
  };
};